import React, {useEffect, useState, useCallback} from 'react';
import $ from "../../utils/datatables";
import {Link} from "react-router-dom";
import { toast } from 'react-toastify';
import {useSelector, useDispatch} from "react-redux";
import feather from "feather-icons";
import {GET_TRADE_OFFERS_BY_COMPANY_URL} from "../../api";
import {fetchAllTradeOffersByCompany, getCurrentUser} from "../../api/web";
import {SET_AUTH_USER} from "../../stores/auth";
import { formatNumber, formatMoney, formatPricePerKg, formatMetricTonnesFromKg, formatQuantityAsMetricTonnes } from "../../utils/formatNumbers";
import { computeTradeOfferStats } from "../../utils/tradeOfferStats";

const TradeOffer = (props) => {
    const currencySymbol = 'ZMW ';

    const token = localStorage.getItem('jwtToken');
    const dispatch = useDispatch();
    const authState = useSelector((state) => state.auth.user);
    const user = authState?.user;
    const [pacraId, setPacraId] = useState(null);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    useEffect(() => {
        if (user?.company?.pacra_id) {
            setPacraId(user.company.pacra_id);
            return;
        }
        if (!token) return;
        getCurrentUser()
            .then((data) => {
                if (data?.company?.pacra_id) {
                    setPacraId(data.company.pacra_id);
                    dispatch(SET_AUTH_USER({ user: data, token: authState?.token || token, isAuthenticated: true }));
                }
            })
            .catch(() => {});
    }, [token, user?.company?.pacra_id, authState?.token, dispatch]);

    const loadTradeOfferStats = useCallback(async () => {
        if (!pacraId) return;
        setStatsLoading(true);
        try {
            const offers = await fetchAllTradeOffersByCompany(pacraId, token);
            setStats(computeTradeOfferStats(offers));
        } catch (e) {
            toast.error('Failed to load trade offer summary', { position: 'top-right', autoClose: 4000 });
            setStats(computeTradeOfferStats([]));
        } finally {
            setStatsLoading(false);
        }
    }, [pacraId, token]);

    useEffect(() => {
        loadTradeOfferStats();
    }, [loadTradeOfferStats]);

    useEffect(() => {
        if (!statsLoading && stats != null) {
            feather.replace();
        }
    }, [statsLoading, stats]);

    useEffect(() => {
        if (!pacraId) return;
        let table = $('#trade_offer_table').DataTable({
            responsive: true,
            language: {
                searchPlaceholder: 'Search',
                sSearch: '',
                lengthMenu: '_MENU_ items/page',
                processing: '<div class="spinner-border"></div><p class="mg-t-10 tx-12">Loading trade offers</p>'
            },
            searching: true,
            lengthMenu: [[10], [10]],
            ordering: false,
            info: true,
            bFilter: false,
            processing: true,
            pageLength: 10,
            serverSide: true,
            ajax: function(data, callback) {
                const page = Math.floor(data.start / data.length) + 1;
                const url = GET_TRADE_OFFERS_BY_COMPANY_URL(pacraId, page, data.length);
                $.ajax(url, {
                    type: 'GET',
                    headers: {
                        Authorization: token ? 'Token ' + token : ''
                    },
                    success: function(res) {
                        const rawList = Array.isArray(res) ? res : (res?.results ?? res?.data ?? []);
                        const result = rawList.map((item) => ({ raw: item }));

                        callback({
                            recordsTotal: res?.count ?? result.length,
                            recordsFiltered: res?.count ?? result.length,
                            data: result
                        });
                    },
                    error: function(err) {
                        toast.error(err.statusText || 'Failed to load trade offers', {position: "top-right", autoClose: 5000});
                        callback({ recordsTotal: 0, recordsFiltered: 0, data: [] });
                    }
                });
            },
            columns: [
                { data: "raw" },
                { data: "raw" },
                { data: "raw" },
                { data: "raw" },
                { data: "raw" },
                { data: "raw" },
                { data: "raw" },
                { data: "raw" }
            ],
            columnDefs: [
                {
                    render: function(data) {
                        const agg = data?.aggregator;
                        return (agg?.name ?? agg?.pacra_id ?? '—');
                    },
                    targets: 0
                },
                {
                    render: function(data) {
                        const crop = data?.crop;
                        return (crop?.name ?? crop ?? '—');
                    },
                    targets: 1
                },
                {
                    render: function(data) {
                        const town = data?.town;
                        return (town?.name ?? town ?? '—');
                    },
                    targets: 2
                },
                {
                    render: function(data) {
                        const statusMap = {
                            OPEN: '<span class="badge bg-success text-white">Open</span>',
                            NEGOTIATION: '<span class="badge bg-warning text-dark">Negotiation</span>',
                            ACCEPTED: '<span class="badge bg-info text-white">Accepted</span>',
                            CLOSED: '<span class="badge bg-dark text-white">Closed</span>'
                        };
                        return statusMap[data?.status] ?? (data?.status ?? '—');
                    },
                    targets: 3
                },
                {
                    render: function(data) {
                        const typeMap = {
                            OPEN: 'Open Market',
                            DIRECT: 'Direct Offer'
                        };
                        return typeMap[data?.offer_type] ?? (data?.offer_type ?? '—');
                    },
                    targets: 4
                },
                {
                    render: function(data) {
                        if (data?.standard_available_quantity != null) {
                            return formatMetricTonnesFromKg(data.standard_available_quantity);
                        }
                        const qty = data?.quantity;
                        const units = data?.units ?? 'kg';
                        return formatQuantityAsMetricTonnes(qty, units);
                    },
                    targets: 5
                },
                {
                    render: function(data) {
                        return data?.price != null ? Number(data.price).toLocaleString() : '—';
                    },
                    targets: 6
                },
                {
                    render: function(data) {
                        return `<a href="#" class="nav-link view_trade_offer"><i class="fa fa-eye"></i> View offer</a>`;
                    },
                    targets: 7
                }
            ]
        });

        table.on('click', '.view_trade_offer', function(e) {
            e.preventDefault();
            let rowData = table.row($(this).closest('tr')).data();
            if (rowData && rowData.raw && rowData.raw.id) {
                props.history.push(`/trade_offer/${rowData.raw.id}`);
            }
            return false;
        });

        return function cleanup() {
            if ($.fn.DataTable.isDataTable('#trade_offer_table')) {
                $('#trade_offer_table').DataTable().destroy();
                $('#trade_offer_table').empty();
            }
        };
    }, [pacraId, token, props.history]);

    const reloadTable = () => {
        loadTradeOfferStats();
        if ($.fn.DataTable.isDataTable('#trade_offer_table')) {
            $('#trade_offer_table').DataTable().ajax.reload(null, false);
        }
    };

    const byStatus = stats?.byStatus ?? {};
    const byType = stats?.byType ?? {};
    const cropMixTop = (stats?.cropMix ?? []).slice(0, 5);

    return (
        <div className="content-body">
            <div className="container pd-x-0 pd-lg-x-10 pd-xl-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Trade Offers</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-0 tx-spacing--1">Trade Offers</h4>
                    </div>
                </div>

                {statsLoading && (
                    <div className="d-flex align-items-center mg-b-15 tx-12 tx-color-03">
                        <div className="spinner-border spinner-border-sm mg-r-10" role="status" />
                        <span>Loading summary…</span>
                    </div>
                )}

                <div className="row g-3">
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-primary tx-semibold mg-b-0">Trade offers</h6>
                                <i data-feather="layers" className="tx-primary" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{stats ? formatNumber(stats.offerCount) : '—'}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-success tx-semibold mg-b-0">Open offers</h6>
                                <i data-feather="check-circle" className="tx-success" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{stats ? formatNumber(stats.openOfferCount) : '—'}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-info tx-semibold mg-b-0">Qty available</h6>
                                <i data-feather="package" className="tx-info" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{stats ? `${formatNumber(stats.totalQtyTonnes)} t` : '—'}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-purple tx-semibold mg-b-0">Listed value</h6>
                                <i data-feather="dollar-sign" className="tx-purple" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{stats ? `${currencySymbol}${formatMoney(stats.totalValue)}` : '—'}</h3>
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">Available qty × listed price</p>
                        </div>
                    </div>
                </div>

                <div className="row g-3 mg-t-20 mg-b-20">
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-warning tx-semibold mg-b-0">Avg listed price</h6>
                                <i data-feather="tag" className="tx-warning" />
                            </div>
                            <div className="d-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 lh-1">{stats ? `${formatPricePerKg(stats.averagePricePerKg)}/kg` : '—'}</h3>
                            </div>
                            <p className="tx-11 tx-color-03 mg-b-0 mt-auto">Weighted by available qty</p>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-secondary tx-semibold mg-b-0">By status</h6>
                                <i data-feather="bar-chart-2" className="tx-secondary" />
                            </div>
                            <div className="tx-12 mg-b-5">
                                <div className="d-flex justify-content-between"><span className="tx-color-03">Open</span><span className="tx-medium">{formatNumber(byStatus.OPEN || 0)}</span></div>
                                <div className="d-flex justify-content-between"><span className="tx-color-03">Negotiation</span><span className="tx-medium">{formatNumber(byStatus.NEGOTIATION || 0)}</span></div>
                                <div className="d-flex justify-content-between"><span className="tx-color-03">Accepted</span><span className="tx-medium">{formatNumber(byStatus.ACCEPTED || 0)}</span></div>
                                <div className="d-flex justify-content-between"><span className="tx-color-03">Closed</span><span className="tx-medium">{formatNumber(byStatus.CLOSED || 0)}</span></div>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-success tx-semibold mg-b-0">By offer type</h6>
                                <i data-feather="git-branch" className="tx-success" />
                            </div>
                            <div className="tx-12 mg-b-5">
                                <div className="d-flex justify-content-between"><span className="tx-color-03">Open market</span><span className="tx-medium">{formatNumber(byType.OPEN || 0)}</span></div>
                                <div className="d-flex justify-content-between"><span className="tx-color-03">Direct</span><span className="tx-medium">{formatNumber(byType.DIRECT || 0)}</span></div>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-info tx-semibold mg-b-0">Top crops by value</h6>
                                <i data-feather="pie-chart" className="tx-info" />
                            </div>
                            <div className="tx-12 mg-b-5" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                                {!stats || cropMixTop.length === 0 ? (
                                    <p className="tx-color-03 mg-b-0">No crop stats</p>
                                ) : (
                                    cropMixTop.map((row, idx) => (
                                        <div key={`${row.label}-${idx}`} className="d-flex justify-content-between mg-b-5">
                                            <span className="tx-color-03">{row.label}</span>
                                            <span className="tx-medium">{currencySymbol}{formatMoney(row.value)}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row row-xs">
                    <div className="col-md-12 mg-t-10">
                        <div className="card">
                            <div className="card-header d-flex align-items-center justify-content-between">
                                <h6 className="mg-b-0">Trade Offers</h6>
                                <div className="d-flex tx-18">
                                    <Link to="#" onClick={reloadTable} className="link-03 lh-0"><i className="icon ion-md-refresh"></i></Link>
                                </div>
                            </div>
                            <div className="card-body table-responsive">
                                <table id="trade_offer_table" className="table table-hover" style={{width: "100%"}}>
                                    <thead>
                                        <tr>
                                            <th className="wd-12p">Aggregator</th>
                                            <th className="wd-12p">Crop</th>
                                            <th className="wd-12p">Town</th>
                                            <th className="wd-10p">Status</th>
                                            <th className="wd-10p">Type</th>
                                            <th className="wd-10p">Qty Available (t)</th>
                                            <th className="wd-10p">Price</th>
                                            <th className="wd-10p">Action</th>
                                        </tr>
                                    </thead>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TradeOffer;
